import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { GlobalContext } from "../../GlobalProvider"; // Import GlobalContext
import { getAppById, createEntity } from "../../api/api"; // Import getAppById for API call

const CreateEntity = () => {
  const { currentApp:currentApp1 } = useContext(GlobalContext); // Get appId from GlobalProvider
  const [currentApp, setCurrentApp] = useState(null); // Store the app data
  const [loading, setLoading] = useState(true); // Loading state
  const [currentEntity, setCurrentEntity] = useState(null); // Current entity being created
  const [entityData, setEntityData] = useState({ name: "" }); // Data for the entity (includes name)
  const [sensorData, setSensorData] = useState({}); // External ID and additional sensor data
  const [error, setError] = useState(null); // Error state

  // Fetch the app data on component mount or when appId changes
  useEffect(() => {
    const fetchAppData = async () => {
      try {
        setLoading(true);
        const app = await getAppById(currentApp1.id); // Fetch app using provided appId
        setCurrentApp(app.data);
      } catch (err) {
        console.error("Error fetching app:", err);
        setError("Failed to fetch app. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (currentApp1) {
      fetchAppData();
    } else {
      setError("App ID is required.");
      setLoading(false);
    }
  }, [currentApp1]);

  if (loading) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="textSecondary">
          Loading App Data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!currentApp) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="textSecondary">
          No App Found. Please select a valid App.
        </Typography>
      </Box>
    );
  }

  if (!currentApp.entities || currentApp.entities.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="textSecondary">
          No Entities to create for the selected App. Please add entities to the App.
        </Typography>
      </Box>
    );
  }

  const handleStartEntityCreation = (entity) => {
    setCurrentEntity(entity);
    const initialData = { name: "" }; // Initialize with a name field
    entity.keys.forEach((key) => (initialData[key] = ""));
    setEntityData(initialData);
    setSensorData({});
  };

  const handleInputChange = (key, value) => {
    setEntityData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSensorDataChange = (sensorId, key, value) => {
    setSensorData((prev) => ({
      ...prev,
      [sensorId]: {
        ...(prev[sensorId] || {}),
        [key]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!entityData.name.trim()) {
      alert("Please provide a name for the entity.");
      return;
    }
  
    // Construct the MongoDB object
    const entityObject = {
      appId: currentApp._id,
      entityTypeId: currentEntity._id,
      name: entityData.name,
      keys: currentEntity.keys.map((key) => ({
        keyName: key,
        value: entityData[key],
      })),
      sensors: currentEntity.sensors.map((sensor) => ({
        sensorId: sensor._id,
        externalId: sensorData[sensor._id]?.externalId || "",
        ...(sensor.sensor_name === "GPS" && currentEntity.geoType === "static"
          ? {
              latitude: sensorData[sensor._id]?.latitude || "",
              longitude: sensorData[sensor._id]?.longitude || "",
            }
          : {}),
      })),
    };
  
    console.log("Submitting Entity:", entityObject);
  
    try {
      // API call to create the entity
      const response = await createEntity(entityObject); // Replace with your actual API function
      alert(`Entity "${entityData.name}" created successfully!`);
      setCurrentEntity(null); // Reset after submission
      setEntityData({ name: "" }); // Reset form fields
      setSensorData({});
    } catch (error) {
      console.error("Error creating entity:", error);
      alert("Failed to create entity. Please try again.");
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Create Entities for {currentApp.name}
      </Typography>
      {!currentEntity ? (
        <>
          <Typography variant="h6" gutterBottom>
            Select an Entity to Create
          </Typography>
          <Grid container spacing={2}>
            {currentApp.entities.map((entity) => (
              <Grid item xs={12} sm={6} md={4} key={entity._id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{entity.name}</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {entity.isGeoLocated ? `Geo-Type: ${entity.geoType}` : "Not Geo-Located"}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleStartEntityCreation(entity)}
                    >
                      Create {entity.name}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <Box>
          <Typography variant="h5" gutterBottom>
            Create {currentEntity.name}
          </Typography>
          <Grid container spacing={2}>
            {/* Entity Name Field */}
            <Grid item xs={12}>
              <TextField
                label="Entity Name"
                value={entityData.name || ""}
                onChange={(e) => handleInputChange("name", e.target.value)}
                fullWidth
                variant="outlined"
                required
              />
            </Grid>
            {currentEntity.keys.map((key) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  label={key}
                  value={entityData[key] || ""}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            ))}
          </Grid>
          <Box mt={3}>
            <Typography variant="h6">Sensors</Typography>
            <List>
              {currentEntity.sensors.map((sensor) => (
                <ListItem key={sensor._id} sx={{ flexDirection: "column", alignItems: "flex-start" }}>
                  <ListItemText
                    primary={`Sensor: ${sensor.sensor_name}`}
                    secondary={`Integration: ${sensor.integration_instruction}`}
                  />
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="External ID"
                        placeholder="Enter External ID"
                        fullWidth
                        value={(sensorData[sensor._id]?.externalId) || ""}
                        onChange={(e) =>
                          handleSensorDataChange(sensor._id, "externalId", e.target.value)
                        }
                      />
                    </Grid>
                    {sensor.sensor_name === "GPS" && currentEntity.geoType === "static" && (
                      <>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Latitude"
                            placeholder="Enter Latitude"
                            fullWidth
                            value={(sensorData[sensor._id]?.latitude) || ""}
                            onChange={(e) =>
                              handleSensorDataChange(sensor._id, "latitude", e.target.value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Longitude"
                            placeholder="Enter Longitude"
                            fullWidth
                            value={(sensorData[sensor._id]?.longitude) || ""}
                            onChange={(e) =>
                              handleSensorDataChange(sensor._id, "longitude", e.target.value)
                            }
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Divider sx={{ my: 2 }} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box mt={3}>
            <Typography variant="h6">Detections</Typography>
            <List>
              {currentEntity.detections.map((detection) => (
                <ListItem key={detection._id}>
                  <ListItemText
                    primary={detection.detection_name}
                    secondary={`Applicable Sensor: ${detection.applicable_sensor}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setCurrentEntity(null)}
            >
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CreateEntity;